import {
    Box,
    Button,
    Divider,
    Flex,
    Icon,
    IconButton,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { TbHistory } from 'react-icons/tb';
import { useExportInventory } from '../../hooks/inventory/useExportInventory';
import type { InventoryExportInput } from '../../types/inventory';

interface InventoryOptionsImportExportProps {
    buildFilteredExportInput: () => InventoryExportInput;
    onRequestImport: () => void;
    onRequestCloseDrawer?: () => void;
    onOpenExportHistory?: () => void;
    onOpenImportHistory?: () => void;
}

export function InventoryOptionsImportExport({
    buildFilteredExportInput,
    onRequestImport,
    onRequestCloseDrawer,
    onOpenExportHistory,
    onOpenImportHistory,
}: InventoryOptionsImportExportProps) {
    const toast = useToast();
    const [exportingMode, setExportingMode] = useState<'filtered' | 'all' | null>(null);
    const exportMutation = useExportInventory({
        onSettled: () => setExportingMode(null),
        onError: (err: unknown) => {
            toast({
                title: 'Export failed',
                description: err instanceof Error ? err.message : 'Could not export inventory.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const handleExport = (mode: 'filtered' | 'all') => {
        const input =
            mode === 'all'
                ? { mode: 'all' as const }
                : buildFilteredExportInput();
        setExportingMode(mode);
        exportMutation.mutate(input);
    };

    const handleRequestImport = () => {
        onRequestCloseDrawer?.();
        onRequestImport();
    };

    const handleOpenExportHistory = () => {
        onRequestCloseDrawer?.();
        onOpenExportHistory?.();
    };

    const handleOpenImportHistory = () => {
        onRequestCloseDrawer?.();
        onOpenImportHistory?.();
    };

    return (
        <Box mt={6}>
            <Divider mb={6} />
            <Flex align="center" justify="space-between" mb={3}>
                <Text fontSize="md" fontWeight="bold" color="gray.900">
                    Export
                </Text>
                <IconButton
                    aria-label="Export history"
                    icon={<Icon as={TbHistory as React.ElementType} />}
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenExportHistory}
                />
            </Flex>
            <VStack spacing={2} align="stretch" mb={6}>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExport('filtered')}
                    isLoading={exportingMode === 'filtered' && exportMutation.isPending}
                >
                    Export with Applied Filters
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExport('all')}
                    isLoading={exportingMode === 'all' && exportMutation.isPending}
                >
                    Export All
                </Button>
            </VStack>

            <Flex align="center" justify="space-between" mb={3}>
                <Text fontSize="md" fontWeight="bold" color="gray.900">
                    Import
                </Text>
                <IconButton
                    aria-label="Import history"
                    icon={<Icon as={TbHistory as React.ElementType} />}
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenImportHistory}
                />
            </Flex>
            <Button
                size="sm"
                variant="outline"
                w="100%"
                onClick={handleRequestImport}
            >
                Import Items
            </Button>
        </Box>
    );
}
